import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Fade,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const PricingChart = () => {
  const theme = useTheme();

  const features = [
    'Focused Task Management',
    'Smart Categories & Filters',
    'Recurring Tasks & Due Dates',
    'Minimal, Calm Design',
    'Priority & Focus Mode',
    'Fast & Secure',
  ];

  const plans = [
    {
      name: 'Trial Phase',
      price: 'Free',
      period: '14 days',
      description: 'Full access to all features',
    },
    {
      name: 'Monthly',
      price: '$2.99',
      period: 'per month',
      description: 'Billed monthly',
    },
    {
      name: 'Annual',
      price: '$24.99',
      period: 'per year',
      description: 'Save 30% vs monthly',
    },
  ];

  return (
    <Box sx={{ bgcolor: '#ffffff', py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 600,
            textAlign: 'center',
            mb: 6,
            color: '#1a1a1a',
            letterSpacing: '-0.01em',
          }}
        >
          Simple, transparent pricing
        </Typography>
        <Box
          sx={{
            bgcolor: '#fafbfc',
            borderRadius: 3,
            p: 4,
            border: '1px solid rgba(0, 0, 0, 0.08)',
            mb: 6,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: 'rgba(0, 0, 0, 0.65)',
              mb: 3,
              fontSize: { xs: '1rem', md: '1.1rem' },
              fontWeight: 500,
            }}
          >
            All plans include full access to all features
          </Typography>
          <Grid container spacing={2}>
            {features.map((feature, featureIndex) => (
              <Grid item xs={12} sm={6} md={4} key={featureIndex}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.primary.main,
                      mr: 1.5,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.65)',
                      fontSize: '0.95rem',
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Fade in timeout={600} style={{ transitionDelay: `${index * 100}ms` }}>
                <Box
                  sx={{
                    bgcolor: '#fafbfc',
                    borderRadius: 3,
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: '#1a1a1a',
                      fontSize: { xs: '1.25rem', md: '1.5rem' },
                    }}
                  >
                    {plan.name}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        fontSize: { xs: '2rem', md: '2.5rem' },
                        lineHeight: 1.2,
                      }}
                    >
                      {plan.price}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(0, 0, 0, 0.65)',
                        fontSize: '0.95rem',
                        mt: 0.5,
                      }}
                    >
                      {plan.period}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.65)',
                      mb: 3,
                      fontSize: '0.95rem',
                    }}
                  >
                    {plan.description}
                  </Typography>
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingChart;
